import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Modifier } from 'draft-js';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import AlignLeft from '../icons/AlignLeft';
import AlignRight from '../icons/AlignRight';
import AlignFull from '../icons/AlignFull';
import AlignCenter from '../icons/AlignCenter';
import Trash from '../icons/Trash';
import Flag from '../icons/Flag';
import withStores from '../stores/withStores';
import adaTheme from '../theme';
const styles = theme => ({
  root: {
    position: 'relative',
    height: '100%',
    userSelect: 'none',
  },
  menu: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%,calc(-100% + 10px))',
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    zIndex: 1,
  },
});

const atomicBlock = (config = {}) => Component => {
  const { alignment = true, feature = false, deletable = true } = config;
  @withStores('appStore', 'pagesStore')
  @observer
  class AtomicBlockComp extends React.Component {
    state = {
      showMenu: false,
      preventClickAway: false,
    };

    handleOpenMenu = e => {
      if (this.state.showMenu) return;
      e.stopPropagation();
      const { appStore } = this.props;
      appStore.setPaused(true);
      this.setState({
        showMenu: true,
      });
    };

    handleCloseMenu = () => {
      if (!this.state.showMenu) return;
      const { appStore } = this.props;
      appStore.setPaused(false);
      this.setState({
        showMenu: false,
        preventClickAway: false,
      });
    };

    handleAlignment = alignment => {
      this.props.pagesStore.currentPage.updateBlockData(this.props.block, {
        alignment,
      });
    };

    handleDelete = () => {
      this.props.pagesStore.currentPage.deleteBlock(this.props.block);
      this.handleCloseMenu();
    };

    handleFeatureImage = () => {
      this.props.pagesStore.currentPage.updatePageData({
        previewImage: this.mediaId,
      });
    };

    handleSetPreventClickAway = state => {
      this.setState({
        preventClickAway: state,
      });
    };

    get currentAlignment() {
      return this.props.block.getData().get('alignment');
    }

    get currentPreviewImage() {
      return this.props.pagesStore.currentPage.data.previewImage;
    }

    get mediaId() {
      const entityKey = this.props.block.getEntityAt(0);
      if (!entityKey) return null;
      const entity = this.props.contentState.getEntity(entityKey);
      if (!entity) return null;
      return entity.data.uuid;
    }

    render() {
      const { appStore, classes, ...restProps } = this.props;
      const { preventClickAway } = this.state;
      if (!appStore.editMode) {
        return <Component {...restProps} isInteractive={this.state.showMenu} />;
      }
      return (
        <ClickAwayListener
          onClickAway={this.handleCloseMenu}
          mouseEvent={preventClickAway ? false : 'onClick'}
        >
          <div
            className={classes.root}
            onClick={this.handleOpenMenu}
            contentEditable={false}
          >
            {this.state.showMenu && (
              <div className={classes.menu}>
                <MuiThemeProvider theme={adaTheme}>
                  {alignment && (
                    <React.Fragment>
                      <IconButton
                        color={
                          this.currentAlignment === 'left'
                            ? 'primary'
                            : 'inherit'
                        }
                        onClick={() => this.handleAlignment('left')}
                      >
                        <AlignLeft fontSize="small" />
                      </IconButton>
                      <IconButton
                        color={
                          this.currentAlignment === 'full'
                            ? 'primary'
                            : 'inherit'
                        }
                        onClick={() => this.handleAlignment('full')}
                      >
                        <AlignFull fontSize="small" />
                      </IconButton>
                      <IconButton
                        color={
                          this.currentAlignment === 'center' ||
                          !this.currentAlignment
                            ? 'primary'
                            : 'inherit'
                        }
                        onClick={() => this.handleAlignment('center')}
                      >
                        <AlignCenter fontSize="small" />
                      </IconButton>
                      <IconButton
                        color={
                          this.currentAlignment === 'right'
                            ? 'primary'
                            : 'inherit'
                        }
                        onClick={() => this.handleAlignment('right')}
                      >
                        <AlignRight fontSize="small" />
                      </IconButton>
                    </React.Fragment>
                  )}
                  {deletable && (
                    <IconButton color="inherit" onClick={this.handleDelete}>
                      <Trash fontSize="small" />
                    </IconButton>
                  )}
                  {feature && (
                    <IconButton
                      color={
                        this.currentPreviewImage === this.mediaId
                          ? 'primary'
                          : 'inherit'
                      }
                      onClick={this.handleFeatureImage}
                    >
                      <Flag fontSize="small" />
                    </IconButton>
                  )}
                </MuiThemeProvider>
              </div>
            )}
            <Component
              {...restProps}
              isInteractive={this.state.showMenu}
              onPreventClickAway={this.handleSetPreventClickAway}
            />
          </div>
        </ClickAwayListener>
      );
    }
  }
  return withStyles(styles)(AtomicBlockComp);
};

export default atomicBlock;
